/* LPC fit finder (snippets/lpc-size-finder.liquid): "Suggest my size" from height, weight and fit
   preference; "Compare measurements" shows the garment table.

   LpcFitFinder.estimate() follows Sloth Hiking Club's estimator for the same tee (Comfort Colors 1717),
   rule for rule, so both shops give the same size for the same person:
   1. Wearer notes within 1 in and 5 lb of the visitor come first (club notes before others).
   2. Otherwise the published size ranges, inclusive, no slack.
   3. Candidates more than one size apart are treated as no match.
   4. No match: ask for the usual unisex size.
   Closer takes the smallest candidate, Relaxed the largest, Oversized the largest plus one.
   A suggested size this product doesn't make is reported, never swapped for another size.
   "Use size X" sends "lpc:select-size" to <lpc-product>, which selects it in the form. */
(() => {
  if (customElements.get('lpc-fit-finder')) return;

  const LB_PER_KG = 2.20462262185;

  class LpcFitFinder extends HTMLElement {
    static estimate(data, height, weight, preference, usual = '') {
      if (!Number.isFinite(height) || !Number.isFinite(weight) || height < 48 || height > 94 || weight < 60 || weight > 600)
        return null;
      const order = data.order;
      const reports = data.reports.filter((r) => Math.abs(r.height - height) <= 1 && Math.abs(r.weight - weight) <= 5);
      const club = reports.filter((r) => r.club);
      const ranges = data.ranges.filter(
        (r) =>
          height >= r.height[0] &&
          (r.height[1] === null || height <= r.height[1]) &&
          weight >= r.weight[0] &&
          weight <= r.weight[1]
      );
      const sizes = club.length
        ? club.flatMap((r) => r.sizes)
        : reports.length
          ? reports.flatMap((r) => r.sizes)
          : ranges.map((r) => r.size);
      const candidates = [...new Set(sizes)].sort((a, b) => order.indexOf(a) - order.indexOf(b));
      if (candidates.length && order.indexOf(candidates[candidates.length - 1]) - order.indexOf(candidates[0]) > 1)
        candidates.length = 0;
      const fromUsual = candidates.length === 0 && order.includes(usual);
      if (fromUsual) candidates.push(usual);
      if (!candidates.length) return { needsUsual: true };
      const base = preference === 'closer' ? candidates[0] : candidates[candidates.length - 1];
      const index = order.indexOf(base) + (preference === 'oversized' ? 1 : 0);
      return {
        size: order[index] || null,
        candidates,
        fromUsual,
        report: (club[0] || reports[0] || {}).copy || '',
        oversized: preference === 'oversized',
      };
    }

    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      try {
        this.evidence = JSON.parse(this.querySelector('[data-fit-evidence]').textContent);
      } catch (e) {
        this.evidence = { order: [], ranges: [], reports: [] };
      }
      this.copy = Object.fromEntries(
        [...this.querySelectorAll('[data-copy-key]')].map((el) => [el.dataset.copyKey, el.textContent.trim()])
      );
      this.unit = 'us';
      this.usual = '';
      this.chosen = '';
      this.form = this.querySelector('[data-fit-form]');
      this.error = this.querySelector('[data-fit-error]');
      this.fallback = this.querySelector('[data-fit-fallback]');
      this.result = this.querySelector('[data-fit-result]');

      this.addEventListener('click', (event) => this.onClick(event));
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit();
      });
      this.form.addEventListener('input', (event) => {
        if (event.target.matches('input[type="number"]')) {
          this.usual = '';
          this.querySelectorAll('[data-fit-usual]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
        }
        this.clearResult();
      });
      this.setGuarantee();
    }

    say(key, replacements = {}) {
      return Object.entries(replacements).reduce(
        (text, [name, value]) => text.split('[' + name + ']').join(value),
        this.copy[key] || ''
      );
    }

    /* The guarantee line repeats whatever the guarantee box on this page shows (snippets/lpc-guarantee.liquid),
       so the two can never make different promises, whether the box runs the A/B test or one fixed wording.
       Only without a box on the page does it fall back to the setting, then to the stored test variant. */
    setGuarantee() {
      const box = this.querySelector('[data-fit-guarantee]');
      if (!box) return;
      const shown = document.querySelector('[data-lpc-guarantee] [data-variant]:not([hidden])');
      let variant = shown ? shown.dataset.variant : box.dataset.mode;
      if (variant !== 'a' && variant !== 'b') {
        variant = 'b';
        try {
          if (localStorage.getItem('lpc_gtee') === 'a') variant = 'a';
        } catch (e) {
          /* storage blocked: current policy text */
        }
      }
      const text = box.dataset['text' + variant.toUpperCase()];
      if (text) box.querySelector('[data-fit-guarantee-text]').textContent = text;
    }

    onClick(event) {
      const target = event.target.closest('button');
      if (!target || !this.contains(target)) return;
      if (target.dataset.fitView) this.showView(target.dataset.fitView);
      else if (target.dataset.fitUnit) this.setUnit(target.dataset.fitUnit);
      else if (target.dataset.fitUsual) {
        this.usual = target.dataset.fitUsual;
        this.querySelectorAll('[data-fit-usual]').forEach((b) => b.setAttribute('aria-pressed', String(b === target)));
        this.render();
      } else if (target.hasAttribute('data-fit-apply') && !target.disabled) this.apply();
    }

    showView(view) {
      this.dataset.view = view;
      this.querySelectorAll('[data-fit-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.fitPanel !== view;
      });
      this.querySelectorAll('.lpc-fit-finder__methods [data-fit-view]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.fitView === view));
      });
    }

    /* Switching units keeps what was typed, converted. */
    setUnit(unit) {
      if (unit === this.unit) return;
      const v = this.values();
      this.unit = unit;
      const metric = unit === 'metric';
      this.querySelectorAll('[data-fit-unit]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.fitUnit === unit));
      });
      const imperial = this.querySelector('[data-fit-imperial]');
      const metricField = this.querySelector('[data-fit-metric]');
      imperial.hidden = metric;
      imperial.querySelectorAll('input').forEach((input) => (input.disabled = metric));
      metricField.hidden = !metric;
      metricField.querySelector('input').disabled = !metric;
      this.querySelector('[data-fit-lb]').hidden = metric;
      this.querySelector('[data-fit-kg]').hidden = !metric;

      const height = Number.isFinite(v.height) ? v.height : null;
      const weight = Number.isFinite(v.weight) ? v.weight : null;
      const total = height === null ? null : Math.round(height);
      this.querySelector('[data-fit-feet]').value = total === null ? '' : String(Math.floor(total / 12));
      this.querySelector('[data-fit-inches]').value = total === null ? '' : String(total % 12);
      this.querySelector('[data-fit-cm]').value = height === null ? '' : String(Number((height * 2.54).toFixed(1)));
      const weightInput = this.querySelector('[data-fit-weight]');
      weightInput.value = weight === null ? '' : String(Number((weight / (metric ? LB_PER_KG : 1)).toFixed(1)));
      weightInput.placeholder = metric ? '70' : '155';
      this.clearResult();
    }

    values() {
      const number = (selector) => {
        const raw = this.querySelector(selector).value.trim();
        return raw === '' ? null : Number(raw);
      };
      const w = number('[data-fit-weight]');
      let height = null;
      if (this.unit === 'us') {
        const f = number('[data-fit-feet]');
        const i = number('[data-fit-inches]');
        if (f !== null && i !== null)
          height = Number.isInteger(f) && Number.isInteger(i) && f >= 4 && f <= 7 && i >= 0 && i <= 11 ? f * 12 + i : NaN;
      } else {
        const c = number('[data-fit-cm]');
        height = c === null ? null : c / 2.54;
      }
      return { height, weight: w === null ? null : w * (this.unit === 'us' ? 1 : LB_PER_KG) };
    }

    preference() {
      const checked = this.form.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : 'relaxed';
    }

    clearResult() {
      this.error.textContent = '';
      this.result.hidden = true;
      this.fallback.hidden = true;
      this.chosen = '';
    }

    submit() {
      this.clearResult();
      const v = this.values();
      if (!Number.isFinite(v.height) || v.height < 48 || v.height > 94) {
        this.error.textContent = 'Enter a valid height, using feet and inches or centimeters.';
        return;
      }
      if (!Number.isFinite(v.weight) || v.weight < 60 || v.weight > 600) {
        this.error.textContent = 'Enter a valid weight in the selected units.';
        return;
      }
      this.render();
    }

    /* The size buttons on the page: value and whether it's sold out in the selected colour. */
    sizeInput(size) {
      const product = this.closest('lpc-product') || document;
      return [...product.querySelectorAll('.lpc-product__option--button .lpc-product__input')].find(
        (input) => input.value.trim().toUpperCase().replace('XXXL', '3XL').replace('XXL', '2XL') === size
      );
    }

    render() {
      const v = this.values();
      const estimate = LpcFitFinder.estimate(this.evidence, v.height, v.weight, this.preference(), this.usual);
      if (!estimate || estimate.needsUsual) {
        this.fallback.hidden = false;
        this.fallback.scrollIntoView({ block: 'nearest' });
        return;
      }
      this.fallback.hidden = !estimate.fromUsual;
      this.chosen = estimate.size;
      const range = estimate.candidates.join(' / ');
      this.querySelector('[data-fit-result-label]').textContent = this.say(estimate.fromUsual ? 'usual_result' : 'estimated');
      this.querySelector('[data-fit-result-size]').textContent = this.chosen || this.say('no_size');
      this.querySelector('[data-fit-result-copy]').textContent = this.say(
        estimate.fromUsual ? 'usual_copy' : estimate.oversized ? 'oversize_result' : estimate.candidates.length > 1 ? 'range' : 'single',
        { range, size: this.chosen || '' }
      );
      this.querySelector('[data-fit-report]').textContent = estimate.report ? this.say(estimate.report) : '';

      const row = this.chosen ? this.querySelector(`tr[data-size="${this.chosen}" i]`) : null;
      this.querySelector('[data-fit-result-measure]').textContent = row
        ? `${this.chosen} measures ${row.dataset.chestIn} in across the chest and ${row.dataset.lengthIn} in long, laid flat.`
        : '';

      const input = this.chosen ? this.sizeInput(this.chosen) : null;
      const unavailable = input ? input.disabled || input.closest('[data-unavailable]') !== null : false;
      this.querySelector('[data-fit-result-stock]').textContent = input
        ? unavailable
          ? this.say('unavailable')
          : ''
        : this.chosen
          ? this.say('not_offered')
          : '';

      const apply = this.querySelector('[data-fit-apply]');
      apply.disabled = !input || unavailable;
      apply.hidden = !this.chosen;
      apply.textContent = this.say('apply', { size: this.chosen || '' });
      this.result.hidden = false;
      this.result.scrollIntoView({ block: 'nearest' });
    }

    apply() {
      if (!this.chosen) return;
      document.dispatchEvent(new CustomEvent('lpc:select-size', { detail: { size: this.chosen } }));
    }
  }

  customElements.define('lpc-fit-finder', LpcFitFinder);
})();
